import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "ui/button";
import { StateSite, createSite } from "lib/actions";
import { useContext, useState } from "react";
import { AuthContext } from "context/AuthContext";
import AddressSearch from "ui/components/address-select";
import React from "react";
import { getTimezoneFromGoogleMapApi } from "pages/dashboard/customers/service";
import { showToast } from "ui/components/common";
import { APIProvider } from "@vis.gl/react-google-maps";

export default function Form({ orgId }: { orgId: string }) {
  interface formDataType {
    [key: string]: FormDataEntryValue;
  }
  const responseBody: formDataType = {};
  const navigate = useNavigate();

  const [responseData, setResponseData] = useState<StateSite>();
  const [btnSubmitDisabled, setBtnSubmitDisabled] = useState(false);
  const [addressCity, setAddressCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [addressZip, setAddressZip] = useState("");
  const [addressLatitude, setAddressLatitude] = useState(0);
  const [addressLongitude, setAddressLongitude] = useState(0);
  const [timezone, setTimezone] = useState("");
  const [address, setAddress] = useState("");

  const hanlePlaceSelect = async (p) => {
    let timezone = "";
    try {
      const res = await getTimezoneFromGoogleMapApi(p.latitude, p.longitude);
      if (res?.status === 200) {
        timezone = res?.data?.timeZoneId;
      }
    } catch (error) {
      showToast(1, "Failed to get timezone");
      console.error("fail to get timezone", error);
    }
    setAddressCity(p.city || "");
    setAddressState(p.state || "");
    setAddressZip(p.zip || "");
    setAddress(p.address);
    setTimezone(timezone);

    setAddressLatitude(p.latitude);
    setAddressLongitude(p.longitude);
  };

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget as HTMLFormElement);
    formData.forEach(
      (value, property: string) => (responseBody[property] = value),
    );

    formData.append("address", address);
    formData.append("latitude", addressLatitude.toString());
    formData.append("longitude", addressLongitude.toString());
    formData.append("city", addressCity);
    formData.append("state", addressState);
    formData.append("zip", addressZip);
    formData.append("timezone", timezone);
    try {
      setBtnSubmitDisabled(true);
      const response = await createSite(orgId, formData);
      setBtnSubmitDisabled(false);
      setResponseData(response);
      if (response.strReturn) {
        const idCreated = response.strReturn;
        navigate(
          `/dashboard/customers/${orgId}/sites/${idCreated}/nvrs/create`,
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <APIProvider
      apiKey={"AIzaSyAqk45RIVytV7tiTjTy2UISpHEgSWjl1vg"}
      onLoad={() => console.log("Maps API has loaded.")}
      onError={(e) => console.log("load error", e)}
    >
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
        <div className="rounded-md">
          <h2 className="text-lg font-medium mb-5">
            Add sites to their account
          </h2>
          <p className="text-sm font-medium mb-5">
            A site is where cameras and NVRs are installed, and alerts are
            generated.
          </p>

          {/* Site Name */}
          <div className="mb-4">
            <label htmlFor="name" className="mb-2 block text-sm">
              Site Name
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Site Name"
                  className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="name-error"
                />
              </div>
              <div id="name-error" aria-live="polite" aria-atomic="true">
                {responseData?.errors?.name &&
                  responseData.errors.name.map((error: string) => (
                    <p className="mt-2 text-sm text-red-500" key={error}>
                      {error}
                    </p>
                  ))}
              </div>
            </div>
          </div>

          {/* Site Address */}
          <div className="mb-4">
            <label htmlFor="address" className="mb-2 block text-sm">
              Address
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <div>
                  <AddressSearch
                    inputClassName={
                      "rounded-md w-full text-sm pl-3 pr-4 py-2 border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-2"
                    }
                    onPlaceSelect={hanlePlaceSelect}
                    placeholder="Enter Address"
                  />
                </div>
              </div>
              <div id="address-error" aria-live="polite" aria-atomic="true">
                {responseData?.errors?.address &&
                  responseData.errors.address.map((error: string) => (
                    <p className="mt-2 text-sm text-red-500" key={error}>
                      {error}
                    </p>
                  ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 md:gap-6">
            {/* City */}
            <div className="mb-4">
              <label htmlFor="city" className="mb-2 block text-sm">
                City
              </label>
              <div className="relative mt-2 rounded-md">
                <div className="relative">
                  <input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="Enter City"
                    className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-500 outline-2"
                    aria-describedby="first-name-error"
                    value={addressCity}
                    onChange={(e) => setAddressCity(e.target.value)}
                  />
                </div>
                <div id="city-error" aria-live="polite" aria-atomic="true">
                  {responseData?.errors?.city &&
                    responseData.errors.city.map((error: string) => (
                      <p className="mt-2 text-sm text-red-500" key={error}>
                        {error}
                      </p>
                    ))}
                </div>
              </div>
            </div>

            {/* State */}
            <div className="mb-4">
              <label htmlFor="state" className="mb-2 block text-sm">
                State
              </label>
              <div className="relative mt-2 rounded-md">
                <div className="relative">
                  <input
                    id="state"
                    name="state"
                    type="text"
                    placeholder="Enter State"
                    className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500 focus-visible:outline-none bg-neutral-100 cursor-not-allowed"
                    aria-describedby="state-error"
                    value={addressState}
                    readOnly
                  />
                </div>
                <div id="state-error" aria-live="polite" aria-atomic="true">
                  {responseData?.errors?.state &&
                    responseData.errors.state.map((error: string) => (
                      <p className="mt-2 text-sm text-red-500" key={error}>
                        {error}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 md:gap-6">
            {/* Zip */}
            <div className="mb-4">
              <label htmlFor="zip" className="mb-2 block text-sm">
                Zip/Postal Code
              </label>
              <div className="relative mt-2 rounded-md">
                <div className="relative">
                  <input
                    id="zip"
                    name="zip"
                    type="text"
                    placeholder="Enter Zip/Postal Code"
                    className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-500 outline-2"
                    aria-describedby="zip-error"
                    value={addressZip}
                    onChange={(e) => setAddressZip(e.target.value)}
                  />
                </div>
                <div id="zip-error" aria-live="polite" aria-atomic="true">
                  {responseData?.errors?.zip &&
                    responseData.errors.zip.map((error: string) => (
                      <p className="mt-2 text-sm text-red-500" key={error}>
                        {error}
                      </p>
                    ))}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="timezone" className="mb-2 block text-sm">
                Timezone
              </label>
              <div className="relative mt-2 rounded-md">
                <div className="relative">
                  <input
                    id="timezone"
                    name="timezone"
                    type="text"
                    placeholder="Enter Timezone"
                    className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500 focus-visible:outline-none bg-neutral-100 cursor-not-allowed"
                    aria-describedby="timezone-error"
                    value={timezone}
                    readOnly
                  />
                </div>
                <div id="timezone-error" aria-live="polite" aria-atomic="true">
                  {responseData?.errors?.timezone &&
                    responseData.errors.timezone.map((error: string) => (
                      <p className="mt-2 text-sm text-red-500" key={error}>
                        {error}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <input
            id="latitude"
            name="latitude"
            type="hidden"
            value={addressLatitude}
          />
          <input
            id="longitude"
            name="longitude"
            type="hidden"
            value={addressLongitude}
          />
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="p-4 flex items-center rounded-2xl bg-gray-100 px-6 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Button type="submit" aria-disabled={btnSubmitDisabled}>
            Continue
          </Button>
        </div>
        {responseData?.message && (
          <div
            className="p-4 mb-4 mt-12 text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 flex items-center space-x-1"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
            <p className="text-sm text-red-500">{responseData.message}</p>
          </div>
        )}
      </form>
    </APIProvider>
  );
}
